import { useState, useEffect } from "react";
import { FORM_MESSAGES, LABELS, UTILITY_CSS_CLASSES } from '../global/constants';
import { 
        makeBEM, 
        fadeOutThenExecute, 
        getDefaultFormMsg, 
        fadeOutFormMsg,
        gracefulFinally,
} from '../global/utils';
import { apiResultSuccess, editBoard, newBoard } from "../apihelper";
import FormStatus from "../atoms/FormStatus";
import FormButton from "../atoms/FormButton";
import { ConfirmDialog } from '../components/Dialogs';

function BoardForm({
                    board,
                    boardTypes=[],
                    discardForm,
                    newSuccess,
                    editSuccess,
                    deleteSuccess,
                }) {
    

    const moduleName = 'board-form';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [formData, setFormData] = useState({ title: '', type_id: 0 });
	const [errors, setErrors] = useState({});
	const [formStatus, setFormStatus] = useState(FORM_MESSAGES.boardFormInstructions);
    const [editMode, setEditMode] = useState(false);
    const [fadeOutTimer, setFadeOutTimer] = useState(false);
    const [typeOptions, setTypeOptions] = useState([]);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const formWrapperClass = `form ${moduleName}`;
    const inputsWrapperClass = `${makeBEM('form', 'inputs-wrapper')} ${makeBEM(moduleName, 'inputs-wrapper')}`;
    const titleErrorClass = errors.title ? UTILITY_CSS_CLASSES.hasErrors : '';
	const titleInputClass = `input ${makeBEM(moduleName, 'title-input')} ${titleErrorClass}`;
    const typeErrorClass = errors.type_id ? UTILITY_CSS_CLASSES.hasErrors : '';
    const typeSelectClass = `select ${makeBEM(moduleName, 'type-select')} ${typeErrorClass} `;
    
    const showDeleteBtn = editMode;
    const btnsDisabled = isSubmitting || isDeleting;

    useEffect(() => {
        if ( board ) {
            setFormData(board);
            setEditMode(true);
        }
    }, [board]);


    useEffect(() => {
        if ( boardTypes ) {
            buildBoardTypeOptions();
        }
    }, [boardTypes]);


    const buildBoardTypeOptions = () => {
        const typeMap = new Map();
        typeMap.set(-1, LABELS.boardType);

        boardTypes.forEach(type => {
            typeMap.set(type.id, type.name);
        });

        setTypeOptions(typeMap);
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedFormData = { ...formData, [name]: value };

        if ( name === 'type_id' ) {
            const parsedValue = parseInt(value);
            updatedFormData.type_id = isNaN(parsedValue) ? value : parsedValue;
        }

        setFormData(updatedFormData);
    }

    const validateForm = () => {
        const newErrors = {};

        if ( !formData.title ) {
            newErrors.title = FORM_MESSAGES.titleRequired;
        }
        
        if ( !formData.type_id || formData.type_id === 0 ) {
            newErrors.type_id = FORM_MESSAGES.typeRequired;
        }

        return newErrors;
    }

    const handleSubmitNew = (postData) => {
        setIsSubmitting(true);
        setFormStatus(FORM_MESSAGES.newBoardSubmit);

        try {

            newBoard(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus(FORM_MESSAGES.createdNewBoard);
                        if ( result.board ) {
                            if ( newSuccess ) {
                                newSuccess(result);

                                setFadeOutTimer(
                                    fadeOutThenExecute(discardForm)
                                );
                            }
                        }
                    }
                },
                postData
            )

        } catch (error) {
            setFormStatus(FORM_MESSAGES.failedSubmit);
        } finally {
            gracefulFinally(setIsSubmitting);
        }
    }

    const handleSubmitEdit = (postData) => {
        setIsSubmitting(true);
        setFormStatus(FORM_MESSAGES.editBoardSubmit);

        postData = { ...postData, board_id: postData.id };

        try {
            editBoard(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus(FORM_MESSAGES.savedBoard);

                        if ( result.board ) {
                            if ( editSuccess ) {
                                editSuccess(result);
                                fadeOutFormMsg(setFormStatus, '');
                            }
                        }
                    }
                },
                postData
            )
        } catch (error) {
            setFormStatus(FORM_MESSAGES.failedSubmit);
        } finally {
            gracefulFinally(setIsSubmitting);
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validateForm();

        if ( Object.keys(validationErrors).length > 0 ) {
            setErrors(validationErrors);
        } else {
            setErrors({});

            const postData = { ...formData };

            if ( !editMode ) {
                handleSubmitNew(postData);
            } else {
                handleSubmitEdit(postData);
            }
        }
    }

    const getFormStatus = () => {
        if ( errors.title ) {
            return errors.title;
        }
        
        if ( errors.type_id ) {
            return errors.type_id;
        }

        if ( formStatus ) {
            return formStatus;
        }

        return getDefaultFormMsg(editMode, FORM_MESSAGES.boardEdit, FORM_MESSAGES.boardFormInstructions);
    }

    const resetForm = () => {
        setFormData({title: '', type_id: '', hidden: false});
        setErrors({});
    }

    const handleDiscard = () => {
        resetForm();
        if ( discardForm ) {
            discardForm();
        }

        if ( fadeOutTimer ) {
            clearTimeout(fadeOutTimer);
        }
    }

    const promptDeleteConfirm = () => {
        setShowConfirmDialog(true);
    }

    const discardConfirmDialog = () => {
        setShowConfirmDialog(false);
    }

    const handleDelete = () => {
        discardConfirmDialog();
        const postData = { ...formData, board_id: formData.id };
        setIsDeleting(true);

        try {
            // TODO: 
            // 1. Call API Here
            // 2. Process results
            // 3. Update form status

            if ( deleteSuccess ) {
                setFadeOutTimer(
                    fadeOutThenExecute(() => {
                        deleteSuccess(postData.board_id)
                    })
                );
            }

        } catch {
            setFormStatus(FORM_MESSAGES.failedSubmit)
        } finally {
            gracefulFinally(setIsDeleting);
        }
    }

    return (
        <div className={ formWrapperClass }>
            <form onSubmit={ handleSubmit }>
                <div className={ inputsWrapperClass  }>
                    <FormStatus
                        errors={errors}
                        formStatus= { getFormStatus() }
                        editMode={editMode}
                        customClass={ makeBEM(moduleName, 'form-status') }
                    />

                    <input
                        id="title"
                        name="title"
                        type="text"
                        className={ titleInputClass }
                        onChange={ handleChange }
                        autoComplete="off"
                        required
                        placeholder={ LABELS.titlePlaceholder }
                        value={ formData.title }
                        autoFocus
                    />

                    <select
                        className={ typeSelectClass }
                        id="type_id"
                        name="type_id"
                        onChange={ handleChange }
                        required
                        value={ formData.type_id }
                        disabled={editMode}
                    >
                        {
                            Array.from(typeOptions.values()).map((value, index) => (
                               <option key={index} value={ parseInt(index)}  disabled= {index === 0}>
                                    {value}
                                </option>
                            ))
                        }
                    </select>

                    <div className={ `${makeBEM('form', 'btns-wrapper')} ${makeBEM(moduleName, 'btns-wrapper')}` }>
                        <FormButton
                            customClass={ makeBEM(moduleName, 'submit-btn') }
                            type="submit"
                            onClick={handleSubmit}
                            disabled={btnsDisabled}
                            isSubmitting={isSubmitting}
                        />
                        <FormButton
                            customClass={ makeBEM(moduleName, 'discard-btn') }
                            onClick={handleDiscard}
                            disabled={btnsDisabled}
                            type="button"
                            icon="close"
                        />
                        {
                            showDeleteBtn &&
                            <FormButton
                                customClass={ makeBEM(moduleName, 'delete-btn') }
                                onClick={promptDeleteConfirm}
                                disabled={btnsDisabled}
                                isSubmitting={isDeleting}
                                type="button"
                                icon="delete"
                            />
                        }
                    </div>
                </div>
            </form>

            {
                showConfirmDialog &&
                <ConfirmDialog
                    show={showConfirmDialog}
                    message={LABELS.confirmDelete}
                    confirmAction={handleDelete}
                    discardAction={discardConfirmDialog}
                />
            }
        </div>
    )
}

export default BoardForm;